import {
	ProductKeys3P,
	type ProductKeys3P as ProductKeys3PType,
} from '@atlassian/rovo-connectors-config/types';

import { type ProductKeys } from '../../common/constants/product-keys';

/**
 * Connector adoption tiers based on analysis of Smart Links usage across sites.
 * https://hello.atlassian.net/wiki/x/eQuDgQE
 *
 * Each connector is assigned a tenant-level usage threshold based on how broadly it is adopted:
 * - Widely adopted connectors: 25% — most sites have significant usage
 * - Mid-adoption connectors: 15% — moderate usage across sites
 * - Niche connectors: 10% — lower baseline usage expected
 */
export type ConnectorAdoptionTier = 'widely-adopted' | 'mid-adoption' | 'niche';
export const ADOPTION_TIER_THRESHOLDS: Record<ConnectorAdoptionTier, number> = {
	'widely-adopted': 25,
	'mid-adoption': 15,
	niche: 10,
};

/**
 * Tier assignments derived from Smart Links usage distribution analysis.
 * https://hello.atlassian.net/wiki/x/eQuDgQE
 */
export const CONNECTORS_BY_ADOPTION_TIER: Record<ConnectorAdoptionTier, Set<ProductKeys3PType>> = {
	'widely-adopted': new Set([
		ProductKeys3P.GoogleDrive,
		ProductKeys3P.Slack,
		ProductKeys3P.Github,
		ProductKeys3P.Figma,
		ProductKeys3P.OneDrive,
		ProductKeys3P.Gitlab,
		ProductKeys3P.AzureDevops,
		ProductKeys3P.Sentry,
		ProductKeys3P.Miro,
		ProductKeys3P.Salesforce,
		ProductKeys3P.Notion,
		ProductKeys3P.Sharepoint,
	]),
	'mid-adoption': new Set([
		ProductKeys3P.Pipedrive,
		ProductKeys3P.Box,
		ProductKeys3P.ClickUp,
		ProductKeys3P.Hubspot,
		ProductKeys3P.Asana,
	]),
	niche: new Set([
		ProductKeys3P.Todoist,
		ProductKeys3P.Zeplin,
		ProductKeys3P.Dropbox,
		ProductKeys3P.Lucid,
		ProductKeys3P.Amplitude,
		ProductKeys3P.Mural,
		ProductKeys3P.Airtable,
		ProductKeys3P.LaunchDarkly,
		ProductKeys3P.Stripe,
		ProductKeys3P.Smartsheet,
		ProductKeys3P.Docusign,
		ProductKeys3P.PagerDuty,
		ProductKeys3P.Teams,
		ProductKeys3P.Dovetail,
		ProductKeys3P.Webex,
	]),
};

// Reverse lookup for quicker/easier product → tier resolution.
const CONNECTOR_TO_TIER = new Map<ProductKeys3PType, ConnectorAdoptionTier>(
	(
		Object.entries(CONNECTORS_BY_ADOPTION_TIER) as [ConnectorAdoptionTier, Set<ProductKeys3PType>][]
	).flatMap(([tier, products]) => [...products].map((product) => [product, tier] as const)),
);

/**
 * Returns the adoption tier for a given connector.
 * Connectors not explicitly listed default to 'niche'.
 */
export const getConnectorAdoptionTier = (product: ProductKeys): ConnectorAdoptionTier => {
	return CONNECTOR_TO_TIER.get(product as ProductKeys3PType) ?? 'niche';
};

/**
 * Returns the tenant-level usage threshold (%) for a given connector.
 * Connectors not mapped to a tier default to the niche threshold.
 */
export const getConnectorUsageThreshold = (product: ProductKeys): number => {
	return ADOPTION_TIER_THRESHOLDS[getConnectorAdoptionTier(product)];
};

/**
 * Returns whether the site has enough Smart Link usage for a connector
 * to justify showing the OAuth banner.
 *
 * Returns `true` (show the banner) if usagePercent is not available
 * since there's no justification to hide it.
 */
export const hasEnoughUsageToShowBanner = (
	product: ProductKeys,
	usagePercent?: number,
): boolean => {
	if (usagePercent === undefined) {
		return true;
	}
	const threshold = getConnectorUsageThreshold(product);
	return usagePercent >= threshold;
};
